import axios from "axios";
import { API_URL, FALLBACK_API_URL } from "../config/apiConfig";

/**
 * Enhanced API utility with retry logic and better error handling
 * This helps resolve production API connection issues
 */

console.log("🔧 API Configuration:");
console.log("   API_URL:", API_URL);
console.log("   FALLBACK_API_URL:", FALLBACK_API_URL);

let currentApiUrl = API_URL;

/**
 * Create an axios instance with interceptors for better error handling
 */
const createApiInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    timeout: 15000, // 15 second timeout
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config) => {
      const corporateToken = localStorage.getItem("corporateToken");
      const regularToken = localStorage.getItem("token");
      const vendorToken = localStorage.getItem("vendorToken");
      const driverToken = localStorage.getItem("driverToken");
      const adminToken = localStorage.getItem("adminToken");

      const token = corporateToken || regularToken || vendorToken || driverToken || adminToken;

      // Debug logging for corporate requests
      if (config.url && config.url.includes("/corporate")) {
        console.log(`🔐 Corporate API Request: ${config.method} ${config.url}`);
        console.log(`   ├─ Corporate Token: ${corporateToken ? "✅ " + corporateToken.substring(0, 20) + "..." : "❌ None"}`);
        console.log(`   ├─ Regular Token: ${regularToken ? "✅ " + regularToken.substring(0, 20) + "..." : "❌ None"}`);
        console.log(`   └─ Final Token Used: ${token ? "✅ " + token.substring(0, 20) + "..." : "❌ None"}`);
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (config.url && config.url.includes("/corporate")) {
        console.warn(`⚠️ No token found for corporate API request: ${config.url}`);
      }

      return config;
    },
    (error) => {
      console.error("❌ Request interceptor error:", error);
      return Promise.reject(error);
    },
  );

  // Response interceptor for better error handling
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url;
      const fullUrl = baseURL + url;

      console.error(
        `❌ API Error: ${status || "Network"} ${url}`,
        error,
      );

      // Provide helpful error messages for specific endpoints
      if (status === 404 && url?.includes("/wallet/verify-topup")) {
        console.error(
          `⚠️ Payment verification endpoint not found!`,
          `   Full URL: ${fullUrl}`,
          `   Status: ${status}`,
          `   This could mean:`,
          `   1. The backend endpoint hasn't been deployed yet`,
          `   2. You need to be authenticated (missing/invalid token)`,
          `   3. The backend server is not running`,
          `   4. There's a mismatch between frontend and backend API URLs`,
        );
      }

      if (status === 401) {
        console.warn(`❌ Authentication failed (401 Unauthorized)`);
        console.warn(`   URL: ${fullUrl}`);
        console.warn(`   Please check your authentication token`);

        // Handle unauthorized - clear tokens
        localStorage.removeItem("corporateToken");
        localStorage.removeItem("token");
        localStorage.removeItem("vendorToken");
        localStorage.removeItem("driverToken");
        localStorage.removeItem("adminToken");
        localStorage.removeItem("user");
        localStorage.removeItem("vendorData");
        localStorage.removeItem("driver");
        localStorage.removeItem("admin");
        localStorage.removeItem("corporateMember");

        const isCorporateRoute = window.location.pathname.startsWith("/corporate");
        if (!window.location.pathname.includes("login")) {
          window.location.href = isCorporateRoute ? "/corporate/login" : "/login";
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

// Create main API instance
let apiInstance = createApiInstance(currentApiUrl);

/**
 * Make an API request with automatic fallback and retry logic
 */
export const apiRequest = async (config) => {
  try {
    // Log full URL for debugging
    const fullUrl = currentApiUrl + config.url;
    console.log(`📍 API Request: ${config.method} ${fullUrl}`);

    const response = await apiInstance(config);
    return response;
  } catch (error) {
    const fullUrl = currentApiUrl + config.url;
    console.error(`❌ API Error: ${error.response?.status || 'Network'} ${fullUrl}`, error.message);

    // If primary API fails and we haven't tried fallback yet
    if (currentApiUrl !== FALLBACK_API_URL) {
      try {
        currentApiUrl = FALLBACK_API_URL;
        apiInstance = createApiInstance(currentApiUrl);

        console.log(`🔄 Retrying with fallback API: ${FALLBACK_API_URL}`);
        const fallbackResponse = await apiInstance(config);
        return fallbackResponse;
      } catch (fallbackError) {
        console.error("❌ Fallback API also failed:", fallbackError);
        throw fallbackError;
      }
    }

    throw error;
  }
};

/**
 * Helper methods for common HTTP operations
 */
export const api = {
  get: (url, config = {}) => apiRequest({ ...config, method: "GET", url }),
  post: (url, data, config = {}) =>
    apiRequest({ ...config, method: "POST", url, data }),
  put: (url, data, config = {}) =>
    apiRequest({ ...config, method: "PUT", url, data }),
  delete: (url, config = {}) =>
    apiRequest({ ...config, method: "DELETE", url }),
  patch: (url, data, config = {}) =>
    apiRequest({ ...config, method: "PATCH", url, data }),
};

/**
 * Get current API URL being used
 */
export const getCurrentApiUrl = () => currentApiUrl;

/**
 * Reset to primary API URL (useful for testing)
 */
export const resetToPrimaryApi = () => {
  currentApiUrl = API_URL;
  apiInstance = createApiInstance(currentApiUrl);
};

/**
 * Test API connectivity
 */
export const testApiConnection = async () => {
  try {
    const response = await apiRequest({
      method: "GET",
      url: "/api-test",
      timeout: 5000,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Test payment wallet endpoints
 */
export const testPaymentEndpoints = async () => {
  console.log("🧪 Testing payment wallet endpoints...");
  console.log(`Current API URL: ${getCurrentApiUrl()}`);

  const endpoints = [
    { method: "POST", url: "/payment/wallet/create-topup-order", requiresAuth: true },
    { method: "POST", url: "/payment/wallet/verify-topup", requiresAuth: true },
  ];

  const results = {};

  for (const endpoint of endpoints) {
    try {
      const response = await apiRequest({
        method: endpoint.method,
        url: endpoint.url,
        data: endpoint.requiresAuth ? { amount: 100 } : undefined,
        timeout: 3000,
      });
      results[endpoint.url] = {
        status: response.status,
        message: "✅ Endpoint exists"
      };
    } catch (error) {
      results[endpoint.url] = {
        status: error.response?.status || "Network Error",
        message: error.message,
      };
    }
  }

  console.log("🧪 Endpoint test results:", results);
  return results;
};

export default api;
